<script setup lang="ts">
defineOptions({
  name: 'AppSelect',
  inheritAttrs: false,
})

type SelectOption = {
  value: string | number
  label: string
  disabled?: boolean
}

const props = withDefaults(
  defineProps<{
    modelValue: string | number | ''
    label?: string
    labelTooltip?: string
    id?: string
    name?: string
    autocomplete?: string
    required?: boolean
    disabled?: boolean
    placeholder?: string
    density?: 'default' | 'compact'
    options?: SelectOption[]
    error?: string | string[] | null
  }>(),
  {
    modelValue: '',
    required: false,
    disabled: false,
    density: 'default',
    options: () => [],
    error: null,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const uid = useId()
const elementId = computed(() => props.id || uid)
const errorId = computed(() => `${elementId.value}-error`)

const errorMessage = computed(() => {
  if (!props.error) return ''
  if (Array.isArray(props.error)) return props.error.find(Boolean) ?? ''
  return props.error
})

const hasError = computed(() => Boolean(errorMessage.value))

const attrs = useAttrs() as Record<string, unknown>
const wrapperClass = computed(() => [
  'app-field',
  props.density === 'compact' ? 'app-field--compact' : undefined,
  attrs.class,
])
const inputAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

function onChange(event: Event) {
  emit('update:modelValue', (event.target as HTMLSelectElement).value)
}
</script>

<template>
  <div :class="wrapperClass">
    <label v-if="label" class="app-field__label" :for="elementId">
      {{ label }}
      <span
        v-if="labelTooltip"
        class="app-field__label-icon"
        :title="labelTooltip"
        :aria-label="labelTooltip"
      >
        <AppIcon name="hero-information-circle" size="sm" />
      </span>
    </label>

    <select
      v-bind="inputAttrs"
      :id="elementId"
      class="app-input"
      :class="{
        'app-input--error': hasError,
        'app-input--compact': density === 'compact',
      }"
      :name="name"
      :autocomplete="autocomplete"
      :required="required"
      :disabled="disabled"
      :value="modelValue"
      :aria-invalid="hasError ? 'true' : undefined"
      :aria-describedby="hasError ? errorId : undefined"
      @change="onChange"
    >
      <option v-if="placeholder" value="" disabled>
        {{ placeholder }}
      </option>

      <option
        v-for="opt in options"
        :key="String(opt.value)"
        :value="opt.value"
        :disabled="opt.disabled"
      >
        {{ opt.label }}
      </option>
    </select>

    <p v-if="hasError" :id="errorId" class="app-field__error">
      {{ errorMessage }}
    </p>
  </div>
</template>
